import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { NestedError } from "abweb/dto/exception/NestedError";
import { RemoteAbInitioErrorException } from "abweb/dto/exception/RemoteAbInitioErrorException";
import { RemoteError } from "abweb/dto/exception/RemoteError";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbInitioStaleDynamicCacheBustParametersError } from "abweb/service/utils/rpc/AbInitioStaleDynamicCacheBustParametersError";
import { FailedToFetchError } from "abweb/service/utils/rpc/FailedToFetchError";
import { NonAbInitioResponseError } from "abweb/service/utils/rpc/NonAbInitioResponseError";
import { UnexpectedResponseContentError } from "abweb/service/utils/rpc/UnexpectedResponseContentError";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorExtra, ErrorUtils } from "abweb/utils/ErrorUtils";
import { TSOGUtils } from "abweb/utils/json/TSOGUtils";
import { LocaleUtils } from "abweb/utils/LocaleUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
import { PromiseUtils } from "abweb/utils/PromiseUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import { VersionUtils } from "abweb/utils/VersionUtils";
// Type declarations are used at compile-time as 'headers' only (does NOT include the files)

const LOG = LogUtils.getLogger(__filename);

/**
 * This line makes a global var visible from a cypress test to know that FetchUtils.ts has loaded.
 * See ab_cy.simulateSlowNetwork_viaFetchUtils
 * Don't copy this pattern!
 * Initializing globals at static scope is in really bad taste.  Talk to Buxbaum.
 */
window.SIMULATE_SLOW_SERVER = false;
export let StatusCode = /*#__PURE__*/function (StatusCode) {
  // Any cancelled request will never have a response (or response header)
  // logins respond with redirect that isn't followed, resulting in status=0 (canceled)
  StatusCode[StatusCode["CANCELLED_REQUEST"] = 0] = "CANCELLED_REQUEST";
  StatusCode[StatusCode["OK"] = 200] = "OK";
  StatusCode[StatusCode["ACCEPTED"] = 202] = "ACCEPTED";
  StatusCode[StatusCode["CACHED"] = 304] = "CACHED";
  StatusCode[StatusCode["BAD_REQUEST"] = 400] = "BAD_REQUEST";
  StatusCode[StatusCode["NOT_FOUND"] = 404] = "NOT_FOUND";
  // Portal can uses a fronting web server (nginx) that is gateway to underlying appserver (running cafe)
  StatusCode[StatusCode["BAD_GATEWAY"] = 502] = "BAD_GATEWAY";
  StatusCode[StatusCode["SERVICE_UNAVAILABLE"] = 503] = "SERVICE_UNAVAILABLE";
  StatusCode[StatusCode["GATEWAY_TIMEOUT"] = 504] = "GATEWAY_TIMEOUT";
  return StatusCode;
}({});

// Fetch is a proposed es7 standard.
// See spec https://fetch.spec.whatwg.org/ and (polyfill WITH EXAMPLES) https://github.com/github/fetch
export class FetchUtils {
  // *********** Start: ReqInit Utils Methods *****************

  static getHeader(reqInit, headerName) {
    return reqInit.headers[headerName /*"Content-Type"*/];
  }
  static setHeader(reqInit, headerName, headerValue) {
    reqInit.headers[headerName /*"Content-Type"*/] = headerValue;
  }
  static removeHeader(reqInit, headerName) {
    delete reqInit.headers[headerName /*"Content-Type"*/];
  }
  static setHeaderIfUnset(reqInit, headerName, headerValue) {
    const headerVal = FetchUtils.getHeader(reqInit, headerName);
    if (headerVal === undefined) {
      FetchUtils.setHeader(reqInit, headerName, headerValue);
    }
  }

  /**
   * Can use this to invoke rpcServices with form-based arguments.
   * The supported parameter types for form-based rpcService requests are primitives: string, number, boolean
   * @param acceptHeader expected return content type, defaults to application/json
   */
  static buildFormRequest(acceptHeader = "application/json") {
    return {
      method: "post",
      headers: {
        "Content-Type": FetchUtils.MEDIA_TYPE_FORM + ";charset=UTF-8",
        // specify UTF-8 to avoid ambiguity, ABSDK-2899
        // "accept-charset": "UTF-8",    // accept-charset had no effect; it is html <form> attrib only
        "Accept": acceptHeader
      }
    };
  }

  /**
   * Can use this to invoke rpcServices with form-based arguments, including File.
   * The supported parameter types for form-based rpcService requests are File and primitives: string, number, boolean
   * Should ONLY use this if some of the args are files to upload, as much less efficient
   * than application/x-www-form-urlencoded.
   * @param acceptHeader expected return content type, defaults to application/json
   */
  static buildMultiPartFormRequest(acceptHeader = "application/json") {
    /*
     * Note that rpc service will actually set the reqInit 'Content-Type' header to undefined before invoking 'fetch'
     * so that fetch will also set the 'boundary' value when defaulting to a multipart content-type
     * (e.g., "Content-Type:multipart/form-data; boundary=----WebKitFormBoundaryyEmKNDsBKjB7QEqu")
     * See  https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-formdata-post
     */
    return {
      method: "post",
      headers: {
        "Content-Type": FetchUtils.MEDIA_TYPE_MULTIPART_FORM,
        // FormData is explicitly UTF-8, and can not be changed.   Do NOT set an encoding.
        // See https://stackoverflow.com/questions/22046930/is-it-possible-to-set-accept-charset-for-new-formdata-xhr2-object-or-workaroun
        "Accept": acceptHeader
      }
    };
  }

  // Edge < 79 does not support calling the File constructor for testing.  This provides a workaround.
  // Can be removed when our Edge min version is >= 79 (also remove
  static fileFactory(fileBits, fileName, fileType = "text/plain;charset=UTF-8") {
    const options = {
      type: fileType
    };
    return new File(fileBits, fileName, options);
  }

  // *********** End: ReqInit Utils Methods *****************

  constructor() {
    // static class, can't create an instance
  }
  static fetchVoid(url, init, fetchConfig = {}, outstandingRequestCounter = null) {
    // No need for a fetchProfiled here: there's no parsing.  (FetchAndCheck does the timing for the request/response round-trip.)
    return FetchUtils.fetchAndCheckResponse(url, init, fetchConfig, outstandingRequestCounter)
    // Cast to Promise<void>
    .then((/*response: Response*/) => Promise.resolve());
  }
  static fetchText(url, init, fetchConfig = {}, outstandingRequestCounter = null, validateResponseFn) {
    return FetchUtils.fetchProfiled(async resp => {
      if (validateResponseFn) {
        validateResponseFn(resp);
      }
      return resp.text();
    }, ":parseToText", url, init, fetchConfig, outstandingRequestCounter).then(retStr => {
      FetchUtils.recordResponseBodyLengthStat(FetchUtils.getProfileRootLabel(fetchConfig, url), retStr != null ? retStr.length : 0);
      return retStr;
    });
  }
  static fetchBlob(url, init, fetchConfig = {}, outstandingRequestCounter = null) {
    return FetchUtils.fetchProfiled(resp => resp.blob(), ":parseToBlob", url, init, fetchConfig, outstandingRequestCounter).then(retBlob => {
      FetchUtils.recordResponseBodyLengthStat(FetchUtils.getProfileRootLabel(fetchConfig, url), retBlob != null ? retBlob.size : 0);
      return retBlob;
    });
  }
  static pollForJsonFile(url, init, pollMsec, abortMSec) {
    return PromiseUtils.poll(() => FetchUtils.fetchAndParseJsonFile(url, init), `json file ${url}`, pollMsec, abortMSec);
  }
  static pollForTextFile(url, init, pollMsec, abortMSec) {
    return PromiseUtils.poll(() => FetchUtils.fetchPlainTextFile(url, init), `text file ${url}`, pollMsec, abortMSec);
  }

  /**
   * @returns json payload from a json file on the backend (served by web server or application server).
   * Uses default caching (with release-specific cache busting) if init.cache is not specified.
   * The url must be for an unsecured json file, which can be served by a fronting web server like nginx
   */
  static fetchAndParseJsonFile(url, init) {
    init = init ?? {};
    init.method = init.method ?? "get";
    init.headers = init.headers ?? {};
    const headers = init.headers;
    headers.accept = headers.accept ?? "application/json";
    return FetchUtils.fetchFile(url, init, "fetchAndParseJsonFile").then(response => response.json());
  }
  static fetchPlainTextFile(url, init) {
    init = init ?? {};
    init.method = init.method ?? "get";
    init.headers = init.headers ?? {};
    const headers = init.headers;
    headers.accept = headers.accept ?? "text/plain";
    headers["Content-type"] = headers["Content-type"] ?? "text/plain; charset=utf-8";
    return FetchUtils.fetchFile(url, init, "fetchPlainTextFile").then(response => response.text());
  }
  static fetchFile(url, init, profileLabel) {
    // if caller provides cache directive (rare), it is on them to ensure correct caching behavior.
    if (!init.cache) {
      init.cache = "default"; // retrieve 'fresh' matches from cache
      url = VersionUtils.cacheBustUrl(url); // always cache bust across releases
    }
    // returned urlParams would ONLY be used to inject RPC_SEQUENCE_NUMBER_PARAM query param value into url,
    // which is not applicable for static json files
    const {
      /*urlParams,*/requestInit
    } = FetchUtils.initializeRequestInit(init);

    // The profiling time includes fetch call (no-op if cached) along with parse time of (possibly cached) response payload.
    return ProfileUtils.elapsedAsync(`${FetchUtils.PROFILE_ROOT}:${profileLabel}:${url}`, async () => window.fetch(url, requestInit).catch(rawFetchError => {
      /*
       * This error is a low-level error thrown during internal 'Fetch' processing. Unfortunately, different
       * browsers throw different (largely undocumented) variants, so this just canonicalizes it to a
       * FailedToFetch error in order to simplify subsequent downstream processing.
       *
       * Note that NO RESPONSE is available (fetch threw before returning a response!)
       */
      throw new FailedToFetchError(rawFetchError);
    }).then(response => {
      // Does NOT reason about returned ab initio headers as do NOT want have to configure nginx to return them!
      if (response.ok /*status == 200s */ || response.status === StatusCode.CACHED || response.status === 0 /* cancelled redirect */) {
        // json files must be well-formed json so can directly use resp.json()
        // rpcs have to worry about empty json payloads meaning 'null'
        return response;
      }
      // Unexpected 'bad' response, so throw most informative error possible
      return FetchUtils.parseServerJsonError(response); // always throws
    }));
  }

  /** @returns json payload that is parsed from response.  Used for api calls that go against the application server. */
  static fetchAndParseJson(url, init, fetchConfig = {}, outstandingRequestCounter = null) {
    const profileRoot = FetchUtils.getProfileRootLabel(fetchConfig, url);
    return FetchUtils.fetchProfiled(resp => FetchUtils.rawJsonParserFn(resp, profileRoot), ":parseToJson", url, init, fetchConfig, outstandingRequestCounter);
  }

  /*
   * Contains any directives that involve interacting with the cache in some way.
   * https://developer.mozilla.org/en-US/docs/Web/API/Request/cache
   */
  static isCachingEnabled(cache) {
    // equivalent:  cachingEnabled:   return cache != undefined && cache != "no-store"
    // equivalent:  cachingDisabled:  return cache == undefined || cache == "no-store"
    return ![undefined, "no-store"].includes(cache);
  }
  static async rawJsonParserFn(resp, profileRoot) {
    // For responses that return non-null payloads, expect a content-type=application/json with a well-formed json response payload
    const contentType = resp.headers.get("content-type"); // case-insensitive
    if (contentType != null && contentType.indexOf(FetchUtils.MEDIA_TYPE_JSON) >= 0) {
      // Removed code that gracefully handled json parsing errors resulting from empty response.body on 10/29/19 */
      const respClone = resp.clone();
      const jsonObj = await resp.json(); // will throw raw json parsing error if not valid
      FetchUtils.recordResponseStats(profileRoot, respClone); // can not pass in 'consumed' resp to stats!
      return jsonObj;
    }

    // When rpc endpoint returns 'null' it short-circuits spring rpc machinery and the response content-type
    // will not be set - see CAT-1174 for details.  However, have seen cases were a
    // proxy (e.g., siteminder, load balancer) takes it upon itself to inject a content-type header
    // if the app server response does not contain one (e.g., text/plain, see CAT-1791).
    // To be more robust, regardless of what content-type header the proxy injects,
    // we'll treat an empty response body as a json 'null' value.
    const contentLength = await FetchUtils.getContentLength(resp);
    if (0 === contentLength) {
      FetchUtils.recordResponseBodyLengthStat(profileRoot, 0);
      return null;
    }
    const errMsg = LocaleUtils.safeResourceLookup(() => AbWebResources.fetchError_receivedBadJsonResponse(String(contentType), String(contentLength)), () => `Expected json response to either have a Content-Type header that contains 'application/json', or for the response body to be empty.  However, received response with 'Content-Type' header: '${contentType}' and response body length: ${contentLength}`);
    throw new UnexpectedResponseContentError(errMsg);
  }
  static async getContentLength(resp) {
    const contentLength = resp.headers.get("content-length"); // case-insensitive
    if (contentLength != null && contentLength.length > 0) {
      return parseInt(contentLength, 10);
    }

    // Chunked http responses may not have a content-length header, so do brute force mechanism
    // Very rare that would get here, but just in case...   Much more inefficient than above due to async text read.
    const respClone = resp.clone(); // clone (which is cheap) needed as reading 'text' consumes the response
    const text = await respClone.text();
    return text.length;
  }
  static fetchProfiled(parseResponseFn, profileTag, url, init, fetchConfig = {}, outstandingRequestCounter = null) {
    // First profile submitting request and getting raw response back.
    return FetchUtils.fetchAndCheckResponse(url, init, fetchConfig, outstandingRequestCounter)
    // then profile parsing the raw response to RetT
    .then(function (response) {
      const profileLabel = FetchUtils.getProfileRootLabel(fetchConfig, url) + profileTag;
      return ProfileUtils.elapsedAsync(profileLabel, () => {
        // fetchAndCheckResponse logs its own errors, so we only need to catch errors thrown by parseResponseFn and log those.
        return parseResponseFn(response).catch(err => FetchUtils.logAndThrowError(url, err)); // always rethrows err
      });
    });
  }
  static fetchAndCheckResponse(url, init, fetchConfig = {}, outstandingRequestCounter = null) {
    const {
      urlParams,
      requestInit
    } = FetchUtils.initializeRequestInit(init);
    const profileRoot = FetchUtils.getProfileRootLabel(fetchConfig, url);
    url = URLUtils.addParamsToUrl(url, urlParams);
    let didDecrementInError = false;
    const callStack = new Error().stack;
    return ProfileUtils.elapsedAsync(() => `${profileRoot}:invoke`, () => {
      // Counter will be decremented on either resolve or reject
      let decrementCounter;
      if (outstandingRequestCounter) {
        outstandingRequestCounter.incrementOutstandingRequestCount();
        decrementCounter = () => outstandingRequestCounter.decrementOutstandingRequestCount();
      }
      // window.SIMULATE_SLOW_SERVER is a piece of state that can be set from within a cypress test.
      const simulateSlowServer = window.SIMULATE_SLOW_SERVER ?? false;
      const delay = simulateSlowServer ? 5000 : 0;
      return PromiseUtils.sleep(delay).then(() => {
        // window.fetch will normalize (uppercase) all methods EXCEPT PATCH ??!!!???
        const origReqInitMethod = requestInit.method;
        try {
          requestInit.method = requestInit.method?.toUpperCase(); // just in case it is PATCH
          return window.fetch(url, requestInit);
        } finally {
          // The rpc might be retried.
          // So undo above uppercasing of requestInit.method as RPCService logic expects original (lowercase) value (get vs GET)
          requestInit.method = origReqInitMethod;
        }
      }).catch(rawFetchError => {
        /*
         * This error is a low-level error thrown during internal 'Fetch' processing. Unfortunately, different
         * browsers throw different (largely undocumented) variants, so this just canonicalizes it to a
         * FailedToFetch error in order to simplify subsequent downstream processing.
         *
         * Note that NO RESPONSE is available (fetch threw before returning a response!)
         */
        throw new FailedToFetchError(rawFetchError);
      }).then(response => {
        return FetchUtils.checkResponseForError(response, url);
      }).then(validatedResponse => {
        decrementCounter?.();
        return validatedResponse;
      }, error => {
        // If a FailedToFetchError was created and thrown above, it is caught and handled here
        if (!didDecrementInError) {
          didDecrementInError = true;
          decrementCounter?.();
          ErrorUtils.setErrorExtra(error, ErrorExtra.rpcServiceErrorUrl, url);
          if (callStack) {
            ErrorUtils.setErrorExtra(error, ErrorExtra.rpcServiceErrorCallStack, callStack);
          }
        }
        return FetchUtils.logAndThrowError(url, error);
      });
    });
  }
  static initializeRequestInit(init) {
    let requestInit;
    if (init == null) {
      requestInit = {
        headers: {}
      };
    } else {
      requestInit = init;
      if (!requestInit.headers) {
        requestInit.headers = {};
      }
    }

    /*
     * Ensure cookies are passed to the server as part of the request. Discuss more intelligent defaulting with
     * Elliot? Use "include" if and only if CORS support is needed, typically POST, PUT or DELETE. Note, however,
     * that POST may be idempotent but used as large args...
     *
     * init.credentials = init.method === "get"
     *    ? "same-origin"
     *    : "include";
     */
    if (requestInit.credentials == null) {
      requestInit.credentials = "same-origin";
    }
    const urlParams = {};
    const headers = requestInit.headers;
    if (FetchUtils.isCachingEnabled(requestInit.cache)) {
      // To be read by PreventCachingFilter.java
      headers[FetchUtils.HeaderNames.Request.AbInitio.Cached] = String(true);
    } else {
      urlParams[FetchUtils.RPC_SEQUENCE_NUMBER_PARAM] = FetchUtils.nextSequenceNum++;
      /*
       * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cache-Control#request_directives
       * https://stackoverflow.com/questions/10314174/difference-between-pragma-and-cache-control-headers
       */
      headers[FetchUtils.HeaderNames.Expires] = -1;
      headers[FetchUtils.HeaderNames.Pragma] = "no-cache";
      headers[FetchUtils.HeaderNames.CacheControl] = FetchUtils.DISABLED_CACHE_CONTROL_REQUEST_HEADER_VALUE;
    }
    return {
      urlParams,
      requestInit
    };
  }

  /**
   * @param profileRoot
   * @param responseClone Caller should pass in a clone of the current responseClone as calculating the body length 'consumes'
   * the responseClone. Needs to have been cloned before the original response has been 'read'.
   */
  static recordResponseStats(profileRoot, responseClone) {
    // Caller needs to clone responseClone as each read 'consumes' the current responseClone
    // Instrumentation for performance measurement showed that cloning is very cheap
    // if (RPCLogger.numClones > 0) { for (let i = 0; i < RPCLogger.numClones; i++) {  let x = origResponse.clone(); } }

    // Do NOT await or return as want this to happen in parallel
    /*await */
    responseClone.text().then(jsonString => {
      // Don't record responseClone length of bad responses (errors)
      FetchUtils.recordResponseBodyLengthStat(profileRoot, jsonString.length);
    }).catch(err => {
      // Ignore any error relating to responseBodyLength calculation
    });
  }
  static recordResponseBodyLengthStat(profileRoot, length) {
    ProfileUtils.add(profileRoot + ":responseBodyLength", "count", length);
  }

  /**
   * Responsible for checking response for error, and if so, rejecting (aka throwing) the
   *  parsed error.  It is NOT responsible for logging the error - the caller should have a promise chain
   *  catch defined that handles the parsed error (probably just log (with error dialog?), but
   *  could also continues promise chain based based on type of error)
   * @param response
   * @param url
   * @return Promise&lt;Response>
   */
  static async checkResponseForError(response, url) {
    // Discuss w/Elliot - there is no valid case where we expect to receive a response from a non-abinitio server,
    // even when supporting 'redirect: follow' as even redirects should be from same app server. See CAT-1311 for details

    const {
      AbInitio
    } = FetchUtils.HeaderNames.Response;

    // Do not check for x-abinitio-response header on responses that won't have it
    // (CAT-1652: NOT_FOUND, ABSDK-2532: *GATEWAY*) so as to not obscure response status with 'missing ab initio header'
    if (
    // Responses with below status codes will never have an ab initio response header (and not expected to!)
    ![StatusCode.NOT_FOUND, StatusCode.BAD_GATEWAY, StatusCode.GATEWAY_TIMEOUT, StatusCode.CANCELLED_REQUEST].includes(response.status) && response.headers.get(AbInitio.Response) !== String(true)) {
      // Typically implies response is not coming from app server (most likely a network proxy, like siteminder or load balancer)
      // Note: during testing, if the application does not initialize cleanly in the app server (no bean errors or other runtime startup errors),
      // tomcat will return an empty response with status 500 for every request.  From the browser perspective,
      // can not tell if the app threw the 500 as there is no X-AbInitio-Response header, or if an intervening proxy
      // threw it.   Conservatively treat it as if a proxy threw it, but this will result in false positives (test triager
      // thinks something is wrong with X-AbInitio-Response header, but really should be looking at the app server log
      // for startup errors.
      const errMsg = LocaleUtils.safeResourceLookup(() => AbWebResources.fetchError_noResponseHeader(AbInitio.Response, url), () => `No '${AbInitio.Response}' header received for url: ${url}`);
      throw new NonAbInitioResponseError(errMsg);
    }
    if (response.headers.get(FetchUtils.HeaderNames.Response.AbInitio.StaleDynamicCacheBustParameters) === String(true)) {
      const currentRemoteDynamicCacheBustParameterValues = await response.json();
      throw new AbInitioStaleDynamicCacheBustParametersError(currentRemoteDynamicCacheBustParameterValues);
    }
    if (response.ok /*status == 200s */ || [StatusCode.CACHED, StatusCode.CANCELLED_REQUEST /* cancelled redirect */].includes(response.status)) {
      return Promise.resolve(response);
    }

    // primarily 400s or 500s
    return FetchUtils.parseServerJsonError(response);
  }
  static parseServerJsonError(response) /* never */
  {
    return response.text().then(function (errText) {
      // Assumption is that app servers will only return RemoteAbInitioErrorException json exceptions
      // when the response has a 4xx or 5xx response code.
      const statusCodeType = Math.floor(response.status / 100);
      //const isInformationalResponse = (statusCodeType === 1);
      //const isSuccess = (statusCodeType === 2);
      //const isRedirection = (statusCodeType === 3);
      const isClientError = statusCodeType === 4;
      const isServerError = statusCodeType === 5;
      if ((isClientError || isServerError) && errText.indexOf("{") === 0) {
        let remoteEx;
        try {
          // will throw if can't parse errText
          remoteEx = FetchUtils.parseServerException(errText);
        } catch (err) {
          const failedToParseMsg = LocaleUtils.safeResourceLookup(AbWebResources.fetchError_unableAbleToParseResponse, "Failed to parse server error from response");
          const fakeRemoteEx = FetchUtils.createRemoteEx(failedToParseMsg, response, errText);
          fakeRemoteEx.messageCode = "fetchError.unableAbleToParseResponse";
          throw new RemoteError(failedToParseMsg, err, response, fakeRemoteEx);
        }
        throw new RemoteError(remoteEx.message ?? "Server Error", null, response, remoteEx);
      } else {
        // Not a 500 error that we parsed so gin up a RemoteAbInitioErrorException and use it
        let message = LocaleUtils.safeResourceLookup(AbWebResources.fetchError_unexpectedServerResponse, "Unexpected server response");
        // Do NOT localize 'Bad Gateway' or 'Gateway Timeout' as they match the response code spec value
        if (response.status === StatusCode.BAD_GATEWAY) message += `: ${StatusCode.BAD_REQUEST} Bad Gateway`;else if (response.status === StatusCode.GATEWAY_TIMEOUT) message += `: ${StatusCode.GATEWAY_TIMEOUT} Gateway Timeout`;
        const fakeRemoteEx = FetchUtils.createRemoteEx(message, response, errText);
        throw new RemoteError(fakeRemoteEx.message, null, response, fakeRemoteEx);
      }
    },
    // Do NOT have this as chained 'catch' as do not want below to handle the
    // thrown errors from above onFulfilled function
    function (getTextErr) {
      const errMsg = LocaleUtils.safeResourceLookup(AbWebResources.fetchError_unableToReadResponse, "Error reading from response");
      // do NOT log as that is the responsibility of downstream catch block that invokes LOGAndThrow
      const fakeRemoteEx = FetchUtils.createRemoteEx(errMsg, response, getTextErr);
      throw new RemoteError(fakeRemoteEx.message, getTextErr, response, fakeRemoteEx);
    });
  }
  static createRemoteEx(message, response, text) {
    const messageArgs = [`URL: ${response.url}`, `type: ${response.type}`, `status: ${response.status}`, `statusText: ${response.statusText}`,
    // The response.redirected flag will indicate when it has been redirected
    // (fetch returns only the 'last' response of a redirect chain)
    `redirected: ${response.redirected}`, text];
    const fakeRemoteEx = RemoteAbInitioErrorException.createRemoteAbInitioErrorException(new Date(), message, messageArgs, `ERROR ${response.status}`, null, null, null);
    return fakeRemoteEx;
  }
  static parseServerException(errText) {
    try {
      return TSOGUtils.decodeObject(JSON.parse(errText), RemoteAbInitioErrorException);
    } catch (err) {
      const errMsg = LocaleUtils.safeResourceLookup(() => AbWebResources.fetchError_unableAbleToParseRemoveAbInitioErrorException(errText), () => `Unable to parse RemoteAbInitioErrorException from following payload: "${errText}"`);
      throw new NestedError(errMsg, err);
    }
  }
  static logAndThrowError(url, err) {
    if (err?.response?.status === StatusCode.NOT_FOUND) {
      if (url.indexOf("/customAssets/") >= 0) {
        // Reduce worrisome noise for PFS client log by having it at LOG.info level (as opposed to LOG.error)
        LOG.info("Request for custom asset '" + url + "' was not found");
        throw err;
      }
      if (url.indexOf("/assets/locale/") >= 0) {
        // thrown error handled by ResourceBundleLoader::loadBundleMap method
        throw err;
      }
    }
    // Make sure orig err is logged (but caller may have additional 'catch' blocks as well
    const errMsg = ErrorUtils.getErrorMsgFromObj(err);
    if (ClassUtils.isInstanceOf(err, AbInitioStaleDynamicCacheBustParametersError)) {
      LOG.info(errMsg);
    } else {
      LOG.error(`Request for url '${url}' failed with following error: \n     ${errMsg}`);
    }
    throw err;
  }
  static getProfileRootLabel(fetchConfig, url) {
    if (!fetchConfig.profileRoot) {
      // first ? or # terminates the root of the url (do NOT want profileRoot to contain request param values)
      const urlRootMatches = url.match(/(.*)[?#]/);
      const urlWithNoParamValues = urlRootMatches ? urlRootMatches[1] : url;
      fetchConfig.profileRoot = FetchUtils.PROFILE_ROOT + ":" + urlWithNoParamValues;
    }
    return fetchConfig.profileRoot;
  }

  /**
   * Returns a promise containing whether a file at the indicated path exists.
   * @param path
   */
  static async fileExists(path) {
    try {
      // throws if file can not be fetched
      await FetchUtils.fetchBlob(path);
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * <p>In general for async fetches, it is considered an error if a redirect is returned.
   * This can happen if a user is not logged in and attempts to retrieve a protected resource,
   * in which case the server redirects to login.jsp (see CAT-1285).
   * </p>
   * <p>So conservatively default to treating redirect as an error (normally 'follow' is the default),
   * and caller can override by explicitly setting the init.redirect value: 'manual', 'follow', 'error'.
   * </p>
   * @param init
   * @param isNewInit
   */

  /*
    private static injectRedirectOption(init: RequestInit, isNewInit: boolean): void
    {
      // Only default it if it isn't already specified in the init
      if (isNewInit || init.redirect == null)
      {
        init.redirect = "error";
      }
    }
  */

  /**
   * Determines whether the error received by call an API is a HTTP 404
   */
  static isResponseError404(e) {
    return e.remoteException && e.remoteException.messageCode && e.remoteException.messageCode === "ERROR 404" || e.response && e.response.status && e.response.status === StatusCode.NOT_FOUND;
  }
}
_defineProperty(FetchUtils, "HeaderNames", {
  CacheControl: "Cache-Control",
  Expires: "Expires",
  Pragma: "Pragma",
  Request: {
    AbInitio: {
      Cached: "X-AbInitio-Cached"
    }
  },
  Response: {
    AbInitio: {
      Response: "X-AbInitio-Response",
      StaleDynamicCacheBustParameters: "X-AbInitio-Stale-Dynamic-Cache-Bust-Parameters"
    }
  }
});
_defineProperty(FetchUtils, "DISABLED_CACHE_CONTROL_REQUEST_HEADER_VALUE", ["no-store", "no-cache", "max-age=0"].join());
// Used for both Content-Type and Accept headers
_defineProperty(FetchUtils, "MEDIA_TYPE_JSON", "application/json");
_defineProperty(FetchUtils, "MEDIA_TYPE_XML", "text/xml");
_defineProperty(FetchUtils, "MEDIA_TYPE_FORM", "application/x-www-form-urlencoded");
_defineProperty(FetchUtils, "MEDIA_TYPE_MULTIPART_FORM", "multipart/form-data");
_defineProperty(FetchUtils, "RPC_SEQUENCE_NUMBER_PARAM", "rpcid");
_defineProperty(FetchUtils, "nextSequenceNum", 0);
_defineProperty(FetchUtils, "PROFILE_ROOT", "FetchUtils");
//# sourceMappingURL=FetchUtils.js.map
